
import { useState } from 'react';
import { SEO } from '../../../utils/seo';
import GlobalNavbar from '../../../components/feature/GlobalNavbar';
import Footer from '../../../components/feature/Footer';

const ProbateContactForm = ({
  formId,
  title,
  subtitle,
}: {
  formId: string;
  title: string;
  subtitle: string;
}) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    propertyAddress: '',
    situation: '',
    timeline: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      formBody.append('phone', formData.phone);
      formBody.append('propertyAddress', formData.propertyAddress);
      formBody.append('situation', formData.situation);
      formBody.append('timeline', formData.timeline);
      formBody.append('message', formData.message);

      await fetch('https://readdy.ai/api/form/d6789skfomltbg1qap9g', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });

      setIsSubmitted(true);
    } catch (error) {
      console.error('Form submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="bg-teal-50 border border-teal-200 rounded-xl p-8 text-center">
        <div className="w-16 h-16 flex items-center justify-center bg-teal-100 rounded-full mx-auto mb-4">
          <i className="ri-check-line text-3xl text-teal-600"></i>
        </div>
        <h3 className="text-2xl font-bold text-gray-900 mb-2">Thank You!</h3>
        <p className="text-gray-600">
          We&apos;ve received your information and will contact you within 24
          hours to discuss your probate property situation.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-xl p-8 border border-gray-100">
      <div className="text-center mb-6">
        <h3 className="text-2xl font-bold text-gray-900 mb-2">{title}</h3>
        <p className="text-gray-600">{subtitle}</p>
      </div>

      <form
        id={formId}
        data-readdy-form
        onSubmit={handleSubmit}
        className="space-y-4"
      >
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Full Name *
            </label>
            <input
              type="text"
              name="name"
              required
              value={formData.name}
              onChange={(e) =>
                setFormData({ ...formData, name: e.target.value })
              }
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
              placeholder="Your full name"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Phone Number *
            </label>
            <input
              type="tel"
              name="phone"
              required
              value={formData.phone}
              onChange={(e) =>
                setFormData({ ...formData, phone: e.target.value })
              }
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
              placeholder="(915) 555-0123"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Email Address *
          </label>
          <input
            type="email"
            name="email"
            required
            value={formData.email}
            onChange={(e) =>
              setFormData({ ...formData, email: e.target.value })
            }
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
            placeholder="your@email.com"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Property Address
          </label>
          <input
            type="text"
            name="propertyAddress"
            value={formData.propertyAddress}
            onChange={(e) =>
              setFormData({ ...formData, propertyAddress: e.target.value })
            }
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
            placeholder="Address of the inherited property"
          />
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Your Situation
            </label>
            <select
              name="situation"
              value={formData.situation}
              onChange={(e) =>
                setFormData({ ...formData, situation: e.target.value })
              }
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm cursor-pointer"
            >
              <option value="">Select situation</option>
              <option value="Executor/Administrator">
                I&apos;m the Executor/Administrator
              </option>
              <option value="Heir/Beneficiary">I&apos;m an Heir/Beneficiary</option>
              <option value="Probate Not Started">
                Probate hasn&apos;t started yet
              </option>
              <option value="Probate In Progress">Probate is in progress</option>
              <option value="Probate Complete">Probate is complete</option>
              <option value="No Will Exists">No will exists (intestate)</option>
              <option value="Other">Other situation</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Timeline
            </label>
            <select
              name="timeline"
              value={formData.timeline}
              onChange={(e) =>
                setFormData({ ...formData, timeline: e.target.value })
              }
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm cursor-pointer"
            >
              <option value="">Select timeline</option>
              <option value="ASAP">Need to sell ASAP</option>
              <option value="1-3 months">Within 1-3 months</option>
              <option value="3-6 months">Within 3-6 months</option>
              <option value="6+ months">6+ months</option>
              <option value="Just exploring">Just exploring options</option>
            </select>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Tell Us More
          </label>
          <textarea
            name="message"
            value={formData.message}
            onChange={(e) =>
              setFormData({
                ...formData,
                message: e.target.value.slice(0, 500),
              })
            }
            rows={3}
            maxLength={500}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm resize-none"
            placeholder="Tell us about your situation and how we can help..."
          />
          <p className="text-xs text-gray-500 mt-1">
            {formData.message.length}/500 characters
          </p>
        </div>

        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full py-4 bg-teal-600 text-white font-semibold rounded-lg hover:bg-teal-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
        >
          {isSubmitting ? (
            <span className="flex items-center justify-center">
              <i className="ri-loader-4-line animate-spin mr-2"></i>
              Submitting...
            </span>
          ) : (
            'Get Your Free Consultation'
          )}
        </button>

        <p className="text-xs text-gray-500 text-center">
          Your information is secure and will never be sold. We&apos;re here to
          help.
        </p>
      </form>
    </div>
  );
};

export default function TexasProbateGuidePage() {
  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Texas Probate Guide Simplified | Understanding Probate in El Paso TX | TREX RE LLC"
        description="A crash course on Texas probate simplified for the average person. Learn about time limits, the probate process, independent vs dependent administration, intestacy, and alternatives like Small Estate Affidavits."
        keywords="Texas probate, probate process Texas, El Paso probate, Letters Testamentary, independent administration, intestate succession Texas, Small Estate Affidavit, Muniment of Title"
        canonical="/elpaso-tx/texas-probate-guide"
        schema={{
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline:
            'A Crash Course on Texas Probate: Simplified for the Average Person',
          description:
            'Comprehensive guide to understanding the Texas probate process, including time limits, administration types, intestacy laws, and alternatives for small estates.',
          author: {
            '@type': 'Organization',
            name: 'TREX RE LLC',
          },
        }}
      />
      <GlobalNavbar />

      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url(https://public.readdy.ai/ai/img_res/cff66f05173526052802a31737aab241.jpg)`,
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/30"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 py-20 w-full">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-block px-4 py-2 bg-teal-500/90 text-white text-sm font-semibold rounded-full mb-6">
                <i className="ri-scales-3-line mr-2"></i>
                Legal Guide for Texas Residents
              </span>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                A Crash Course on{' '}
                <span className="text-teal-400">Texas Probate</span>
              </h1>
              <p className="text-xl text-gray-200 mb-8 leading-relaxed">
                Simplified for the average person to understand. Learn
                everything you need to know about navigating the probate process
                in Texas without the confusing legal jargon.
              </p>

              <div className="flex flex-wrap gap-4 mb-8">
                <div className="flex items-center text-white">
                  <i className="ri-check-line text-teal-400 mr-2"></i>
                  <span>Plain English Explanations</span>
                </div>
                <div className="flex items-center text-white">
                  <i className="ri-check-line text-teal-400 mr-2"></i>
                  <span>Texas-Specific Laws</span>
                </div>
                <div className="flex items-center text-white">
                  <i className="ri-check-line text-teal-400 mr-2"></i>
                  <span>Practical Guidance</span>
                </div>
              </div>
            </div>

            <div>
              <ProbateContactForm
                formId="hero-probate-form"
                title="Need Help With a Probate Property?"
                subtitle="We buy inherited properties in any condition"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <article className="max-w-4xl mx-auto px-6 py-16">
        {/* Introduction */}
        <div className="prose prose-lg max-w-none mb-12">
          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            If you&apos;ve recently lost a loved one, the last thing you want
            to deal with is complicated legal procedures. Unfortunately, if
            that person owned property or had assets in Texas, you&apos;ll
            likely encounter something called &quot;probate.&quot; The word
            itself sounds intimidating, and the process can feel overwhelming
            — especially when you&apos;re already grieving. But here&apos;s
            the good news: probate doesn&apos;t have to be as scary as it
            sounds.
          </p>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            This guide breaks down the Texas probate process into simple,
            understandable terms. Whether you&apos;re an executor named in a
            will, an heir wondering what happens next, or someone trying to
            help a family member navigate this process, we&apos;ll walk you
            through everything step by step. By the end of this article, you&apos;ll
            understand what probate is, why it exists, how it works in Texas,
            and what alternatives might be available to you.
          </p>

          <p className="text-lg text-gray-700 leading-relaxed mb-8">
            At TREX RE LLC, we&apos;ve helped countless families in El Paso
            and throughout Texas deal with inherited properties during the
            probate process. We understand that selling a home during this
            difficult time can add stress to an already challenging
            situation. That&apos;s why we&apos;ve created this comprehensive
            guide—to empower you with knowledge and help you make informed
            decisions about your inherited property.
          </p>
        </div>

        {/* What is Probate Section */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            What Exactly Is Probate?
          </h2>

          <img
            src="https://readdy.ai/api/search-image?query=legal%20documents%20last%20will%20and%20testament%20on%20wooden%20desk%20with%20reading%20glasses%20pen%20and%20family%20photos%20in%20background%20warm%20natural%20lighting%20professional%20photography%20showing%20estate%20planning%20concept&width=800&height=500&seq=probate-docs-002&orientation=landscape"
            alt="Legal documents and last will and testament"
            className="w-full h-96 object-cover rounded-lg shadow-lg mb-8"
          />

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            Let&apos;s start with the basics. Probate is simply the legal
            process of settling a deceased person&apos;s estate. Think of it
            as the official, court-supervised way of making sure a person&apos;s
            final wishes are carried out and their belongings go to the right
            people. The court&apos;s job is to verify that any will is
            legitimate, ensure debts and taxes are paid, and oversee the
            distribution of assets to the rightful heirs or beneficiaries.
          </p>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            Why does probate exist? Without it, there would be chaos. Imagine
            if anyone could simply claim ownership of a deceased person&apos;s
            house or empty their bank accounts. Probate provides a structured,
            legal framework that protects everyone involved—the deceased
            person&apos;s wishes, the rights of creditors who are owed money,
            and the interests of heirs and beneficiaries.
          </p>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            In Texas, probate is handled by county courts, typically the
            Statutory Probate Court or the County Court at Law, depending on
            where the deceased person lived. The process involves filing
            paperwork, attending court hearings, and following specific legal
            procedures. While this might sound complicated, Texas actually
            has one of the more streamlined probate systems in the country —
            especially compared to states like California or New York.
          </p>

          <div className="bg-teal-50 border-l-4 border-teal-600 p-6 rounded-lg mb-6">
            <h4 className="text-xl font-bold text-gray-900 mb-3">
              <i className="ri-lightbulb-line mr-2 text-teal-600"></i>
              Key Point to Remember
            </h4>
            <p className="text-gray-700">
              Not all assets go through probate. Things like life insurance
              policies with named beneficiaries, retirement accounts with
              designated beneficiaries, jointly-owned property with right of
              survivorship, and assets held in trusts typically pass directly
              to beneficiaries without probate. This is important to understand
              when assessing what actually needs to go through the probate
              process.
            </p>
          </div>
        </section>

        {/* Time Limit Section */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            The Critical Four-Year Time Limit
          </h2>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            Here&apos;s something many people don&apos;t realize until it&apos;s
            almost too late: <strong>in Texas, a will must generally be filed
            for probate within four years of the date of death</strong>. This
            is one of the most important deadlines in Texas probate law,
            and missing it can create serious complications.
          </p>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            Why does this deadline matter so much? If you wait longer than
            four years to probate a will, the court may refuse to admit it to
            probate. When this happens, the estate is treated as if the
            person died without a will (called &quot;intestate&quot;), and
            Texas intestacy laws determine who inherits the assets —
            which may not match what the deceased person actually wanted.
          </p>

          <img
            src="https://readdy.ai/api/search-image?query=calendar%20with%20marked%20deadline%20date%20and%20clock%20showing%20urgency%20important%20legal%20deadline%20concept%20with%20documents%20and%20pen%20on%20clean%20desk%20professional%20business%20photography%20warm%20lighting&width=800&height=500&seq=probate-deadline-003&orientation=landscape"
            alt="Calendar showing important deadline"
            className="w-full h-96 object-cover rounded-lg shadow-lg mb-8"
          />

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            There is one exception to this rule. If you can prove to the court
            that you weren&apos;t &quot;in default&quot; for failing to present
            the will within four years — meaning you had a valid reason for the
            delay and weren&apos;t just neglecting your responsibilities —
            the court may still accept the will. However, this requires
            additional legal proceedings and isn&apos;t guaranteed.
          </p>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            The practical takeaway? Don&apos;t wait. If a loved one has
            passed away and left a will, start the probate process as soon as
            reasonably possible. Even if you&apos;re not ready to deal with
            selling property or distributing assets, at least get the will
            filed with the court to preserve your rights. Many families
            dealing with{' '}
            <a
              href="/inherited-homes"
              className="text-teal-600 hover:text-teal-700 font-medium"
            >
              inherited homes
            </a>{' '}
            discover this deadline when they&apos;re finally ready to sell,
            only to find they&apos;ve created unnecessary complications by
            waiting.
          </p>

          <div className="bg-amber-50 border-l-4 border-amber-500 p-6 rounded-lg mb-6">
            <h4 className="text-xl font-bold text-gray-900 mb-3">
              <i className="ri-error-warning-line mr-2 text-amber-500"></i>
              Warning: Don&apos;t Miss This Deadline
            </h4>
            <p className="text-gray-700">
              We&apos;ve seen families lose significant inheritance rights
              because they waited too long to file. If you&apos;re approaching
              the four-year mark, consult with a probate attorney
              immediately. The cost of legal help now is far less than the
              potential loss of inheritance later.
            </p>
          </div>
        </section>

        {/* The Probate Process Section */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            The Texas Probate Process: Step by Step
          </h2>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            Now let&apos;s walk through what actually happens during probate.
            While every situation is unique, the basic process follows a
            predictable pattern. Understanding these steps will help you know
            what to expect and prepare accordingly.
          </p>

          <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
            Step 1: Filing the Application
          </h3>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            The probate process begins when someone (usually the person named
            as executor in the will, or an interested party if there&apos;s
            no will) files an &quot;Application to Probate Will&quot; with the
            appropriate county court. This application includes basic
            information about the deceased person, their heirs, and the assets
            in the estate. The original will must be filed along with this
            application.
          </p>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            In Texas, you file in the county where the deceased person lived
            (their &quot;domicile&quot;). If they owned property in multiple
            counties, you still file in their home county, but you may need to
            file additional paperwork in other counties where they owned real
            estate.
          </p>

          <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
            Step 2: Posting and Waiting Period
          </h3>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            After the application is filed, the court requires a waiting period
            before the hearing. A citation must be posted at the courthouse for
            at least 10 days, giving notice to anyone who might want to contest
            the will or the appointment of the executor. This waiting period
            protects the rights of all interested parties.
          </p>

          <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
            Step 3: The Probate Hearing
          </h3>

          <img
            src="https://readdy.ai/api/search-image?query=courtroom%20interior%20with%20judge%20bench%20wooden%20furniture%20american%20flag%20texas%20state%20flag%20professional%20legal%20setting%20warm%20lighting%20showing%20formal%20court%20proceedings&width=800&height=500&seq=probate-court-004&orientation=landscape"
            alt="Texas courtroom interior"
            className="w-full h-96 object-cover rounded-lg shadow-lg mb-8"
          />

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            After the waiting period, a hearing is scheduled. Don&apos;t worry
            — this isn&apos;t like what you see on TV courtroom dramas.
            Probate hearings are typically brief and straightforward. The
            judge will verify that the will is valid, confirm that proper notice
            was given, and ensure the proposed executor is qualified to serve.
          </p>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            At the hearing, someone (usually the executor or a witness to the
            will) must testify that the will is genuine and was properly
            executed. If the will is &quot;self-proved&quot; (meaning it
            includes a special notarized affidavit signed when the will was
            created), this testimony requirement is simplified.
          </p>

          <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
            Step 4: Letters Testamentary
          </h3>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            If everything checks out, the court issues &quot;Letters
            Testamentary&quot; to the executor. This is essentially the
            executor&apos;s official authorization to act on behalf of the
            estate. With Letters Testamentary in hand, the executor can
            access bank accounts, transfer property titles, pay debts, and
            distribute assets to beneficiaries.
          </p>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            Think of Letters Testamentary as your &quot;permission slip&quot;
            from the court. Banks, title companies, and other institutions
            will require certified copies of this document before they&apos;ll
            work with you on estate matters. You&apos;ll likely need multiple
            certified copies, so request several from the court clerk.
          </p>

          <div className="bg-gray-50 rounded-lg p-6 mb-6">
            <h4 className="text-xl font-bold text-gray-900 mb-4">
              Texas Probate Process Timeline
            </h4>
            <div className="space-y-4">
              {[
                ['File Application', 'Submit application and original will to county court'],
                ['10+ Day Waiting Period', 'Citation posted at courthouse for public notice'],
                ['Court Hearing', 'Judge validates will and confirms executor'],
                ['Letters Testamentary Issued', 'Executor receives official authority to act'],
                ['Estate Administration', 'Pay debts, manage assets, distribute to beneficiaries'],
              ].map(([title, desc], idx) => (
                <div className="flex items-start" key={idx}>
                  <div className="w-8 h-8 flex items-center justify-center bg-teal-100 rounded-full mr-4 flex-shrink-0">
                    <span className="text-teal-600 font-bold text-sm">{idx + 1}</span>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">{title}</p>
                    <p className="text-gray-600 text-sm">{desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Independent vs Dependent Administration */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            Independent vs. Dependent Administration: What&apos;s the Difference?
          </h2>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            One of the most important concepts in Texas probate is the
            distinction between &quot;independent&quot; and &quot;dependent&quot;
            administration. This determines how much court oversight the
            executor will face and significantly impacts the time, cost, and
            complexity of settling the estate.
          </p>

          <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
            Independent Administration (The Preferred Option)
          </h3>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            <strong>Independent administration is by far the most common type
            in Texas</strong>, and for good reason — it&apos;s faster, cheaper,
            and less burdensome. Under independent administration, the executor
            can manage the estate with minimal court supervision. They can pay
            bills, sell property, distribute assets, and handle most matters
            without getting court approval for each action.
          </p>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            For independent administration to apply, one of the following
            must be true: the will specifically grants independent administration
            powers, all heirs agree to independent administration, or the
            court determines it&apos;s appropriate. Most well-drafted wills in
            Texas include language granting independent administration because
            attorneys know how much easier it makes the process.
          </p>

          <img
            src="https://readdy.ai/api/search-image?query=professional%20executor%20reviewing%20estate%20documents%20at%20desk%20with%20laptop%20calculator%20and%20legal%20papers%20organized%20workspace%20warm%20natural%20lighting%20showing%20estate%20administration%20work&width=800&height=500&seq=probate-admin-005&orientation=landscape"
            alt="Executor reviewing estate documents"
            className="w-full h-96 object-cover rounded-lg shadow-lg mb-8"
          />

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            The executor still has legal duties and responsibilities — they must
            act in the best interests of the estate and beneficiaries, keep
            accurate records, and can be held liable for mismanagement. But they
            don&apos;t need to file detailed accountings with the court or get
            permission before taking routine actions. This streamlined approach
            is one reason Texas is considered a relatively &quot;probate-
            friendly&quot; state.
          </p>

          <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
            Dependent Administration (Court-Supervised)
          </h3>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            Dependent administration is the opposite — the executor (called an
            &quot;administrator&quot; in this context) must get court approval
            for virtually every significant action. Want to sell a piece of
            property? You need court permission. Want to pay a large debt? Court
            permission. Want to distribute assets to heirs? You guessed it —
            court permission.
          </p>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            This level of oversight exists to protect beneficiaries when
            there&apos;s concern about potential mismanagement or disputes.
            Dependent administration might be required when the will doesn&apos;t
            grant independent powers and heirs can&apos;t agree, when there are
            concerns about the executor&apos;s ability to manage the estate
            properly, or when the court determines closer supervision is
            needed.
          </p>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            The downside? Dependent administration takes longer and costs more
            due to additional court filings, hearings, and attorney fees. If
            you&apos;re dealing with a dependent administration and need to
            sell inherited property, the process will involve extra steps and
            delays. Understanding{' '}
            <a
              href="/title-issues"
              className="text-teal-600 hover:text-teal-700 font-medium"
            >
              title issues
            </a>{' '}
            becomes especially important in these situations.
          </p>

          <div className="overflow-x-auto mb-8">
            <table className="w-full border-collapse bg-white rounded-lg overflow-hidden shadow-lg">
              <thead>
                <tr className="bg-gray-900 text-white">
                  <th className="p-4 text-left">Factor</th>
                  <th className="p-4 text-left">
                    Independent Administration
                  </th>
                  <th className="p-4 text-left">Dependent Administration</th>
                </tr>
              </thead>
              <tbody className="text-gray-700">
                <tr className="border-b">
                  <td className="p-4 font-medium">Court Oversight</td>
                  <td className="p-4">Minimal</td>
                  <td className="p-4">Extensive</td>
                </tr>
                <tr className="border-b bg-gray-50">
                  <td className="p-4 font-medium">Selling Property</td>
                  <td className="p-4">No court approval needed</td>
                  <td className="p-4">Court approval required</td>
                </tr>
                <tr className="border-b">
                  <td className="p-4 font-medium">Timeline</td>
                  <td className="p-4">Faster (months)</td>
                  <td className="p-4">Slower (can take years)</td>
                </tr>
                <tr className="border-b bg-gray-50">
                  <td className="p-4 font-medium">Cost</td>
                  <td className="p-4">Lower</td>
                  <td className="p-4">Higher (more court fees)</td>
                </tr>
                <tr className="border-b">
                  <td className="p-4 font-medium">Flexibility</td>
                  <td className="p-4">High</td>
                  <td className="p-4">Limited</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="p-4 font-medium">Common In Texas?</td>
                  <td className="p-4">Yes (most estates)</td>
                  <td className="p-4">Less common</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        {/* Middle Lead Capture Form */}
        <section className="mb-16">
          <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-8 md:p-12">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div className="text-white">
                <h2 className="text-3xl font-bold mb-4">
                  Dealing With Inherited Property During Probate?
                </h2>
                <p className="text-gray-300 mb-6">
                  We specialize in helping families sell inherited properties —
                  even during the probate process. Get a fair cash offer with
                  no obligation.
                </p>
                <ul className="space-y-3">
                  <li className="flex items-center">
                    <i className="ri-check-line text-teal-400 mr-3"></i>
                    <span>We understand probate timelines</span>
                  </li>
                  <li className="flex items-center">
                    <i className="ri-check-line text-teal-400 mr-3"></i>
                    <span>Buy properties in any condition</span>
                  </li>
                  <li className="flex items-center">
                    <i className="ri-check-line text-teal-400 mr-3"></i>
                    <span>Work with your attorney</span>
                  </li>
                  <li className="flex items-center">
                    <i className="ri-check-line text-teal-400 mr-3"></i>
                    <span>Close when probate allows</span>
                  </li>
                </ul>
              </div>
              <div>
                <ProbateContactForm
                  formId="middle-probate-form"
                  title="Get Your Cash Offer"
                  subtitle="No obligation, no pressure"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Intestate Succession Section */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            What Happens When There&apos;s No Will? (Intestate Succession)
          </h2>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            Not everyone leaves a will. In fact, studies suggest that more than
            half of American adults don&apos;t have one. When someone dies
            without a valid will, they&apos;re said to have died
            &quot;intestate,&quot; and Texas law determines who inherits their
            property through a set of rules called &quot;intestate succession.&quot;
          </p>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            Instead of an executor named in a will, the court appoints an
            &quot;administrator&quot; to manage the estate. This person is
            typically a close family member — usually a surviving spouse or
            adult child. The administrator has similar duties to an executor
            but operates under dependent administration rules, meaning more
            court oversight.
          </p>

          <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
            How Texas Intestate Succession Works
          </h3>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            Texas intestate succession rules can get complicated, especially
            when dealing with community property (assets acquired during
            marriage) versus separate property (assets owned before marriage or
            received as gifts/inheritance). Here&apos;s a simplified overview:
          </p>

          <div className="space-y-4 mb-8">
            <div className="bg-gray-50 p-5 rounded-lg">
              <h4 className="font-bold text-gray-900 mb-2">
                If Married with Children (All from This Marriage)
              </h4>
              <p className="text-gray-700 text-sm">
                Surviving spouse inherits all community property and 1/3 of
                separate personal property (with 2/3 going to children). For
                separate real property, spouse gets a life estate in 1/3, with
                children inheriting the rest.
              </p>
            </div>
            <div className="bg-gray-50 p-5 rounded-lg">
              <h4 className="font-bold text-gray-900 mb-2">
                If Married with Children from Previous Relationship
              </h4>
              <p className="text-gray-700 text-sm">
                The deceased&apos;s share of community property goes to the
                children, not the surviving spouse. This often surprises families
                and can create complicated situations.
              </p>
            </div>
            <div className="bg-gray-50 p-5 rounded-lg">
              <h4 className="font-bold text-gray-900 mb-2">
                If Married with No Children
              </h4>
              <p className="text-gray-700 text-sm">
                Surviving spouse inherits all community property and all
                separate personal property. For separate real property, spouse
                gets 1/2 and the other 1/2 goes to the deceased&apos;s parents,
                siblings, or their descendants.
              </p>
            </div>
            <div className="bg-gray-50 p-5 rounded-lg">
              <h4 className="font-bold text-gray-900 mb-2">
                If Single with Children
              </h4>
              <p className="text-gray-700 text-sm">
                Children inherit everything equally. If a child has passed
                away, their share goes to their children (the deceased&apos;s
                grandchildren).
              </p>
            </div>
            <div className="bg-gray-50 p-5 rounded-lg">
              <h4 className="font-bold text-gray-900 mb-2">
                If Single with No Children
              </h4>
              <p className="text-gray-700 text-sm">
                Property goes to parents, then siblings, then more distant
                relatives following a specific order defined by Texas law.
              </p>
            </div>
          </div>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            As you can see, intestate succession can lead to outcomes the
            deceased person might not have wanted. This is why estate planning
            attorneys always recommend having a will — it ensures your wishes
            are followed rather than leaving decisions to state law. If you&apos;re
            dealing with an intestate estate and considering selling the
            property, you may want to explore options for{' '}
            <a
              href="/avoid-foreclosure"
              className="text-teal-600 hover:text-teal-700 font-medium"
            >
              avoiding foreclosure
            </a>{' '}
            if there are outstanding debts or mortgage payments that heirs
            cannot maintain.
          </p>
        </section>

        {/* Alternatives to Full Probate */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            Alternatives to Full Probate Administration
          </h2>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            Here&apos;s some good news: not every estate needs to go through the
            full probate process. Texas offers several simplified alternatives
            for smaller or less complicated estates. These options can save
            significant time and money while still legally transferring assets
            to the rightful heirs.
          </p>

          <img
            src="https://readdy.ai/api/search-image?query=family%20meeting%20with%20attorney%20in%20professional%20office%20discussing%20estate%20documents%20and%20legal%20papers%20warm%20lighting%20comfortable%20setting%20showing%20consultation%20and%20guidance&width=800&height=500&seq=probate-meeting-006&orientation=landscape"
            alt="Family meeting with attorney"
            className="w-full h-96 object-cover rounded-lg shadow-lg mb-8"
          />

          <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
            Small Estate Affidavit
          </h3>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            If the total value of the estate (excluding homestead and exempt
            property) is <strong>$75,000 or less</strong>, heirs may be able to
            use a Small Estate Affidavit instead of going through probate.
            This is a sworn statement filed with the court that identifies the
            heirs and the assets, allowing property to be transferred without
            full administration.
          </p>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            To use a Small Estate Affidavit, several conditions must be met:
            30 days must have passed since the death, no petition for probate is
            pending, the estate value qualifies, the estate isn&apos;t insolvent
            (debts don&apos;t exceed assets), and all heirs agree to the
            distribution. Two disinterested witnesses must also sign the
            affidavit.
          </p>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            The Small Estate Affidavit is particularly useful for transferring
            vehicles, bank accounts, and other personal property. However, it
            has limitations when it comes to real estate — many title companies
            are hesitant to rely solely on a Small Estate Affidavit for
            property transfers, which is why the next option is often preferred
            for real property.
          </p>

          <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
            Muniment of Title
          </h3>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            Muniment of Title is a streamlined probate procedure that&apos;s
            perfect when there&apos;s a valid will, no unpaid debts (other than
            debts secured by real estate), and the primary goal is to transfer
            real property to beneficiaries. It&apos;s essentially a way to use
            the will as direct proof of ownership transfer without appointing an
            executor or going through full administration.
          </p>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            The process involves filing an application with the court, having a
            brief hearing where the judge validates the will, and receiving a
            court order that can be recorded in the county property records.
            This order serves as the legal link in the chain of title,
            allowing the property to be sold or transferred.
          </p>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            Muniment of Title is faster and less expensive than full probate
            administration. There&apos;s no need to post bond, file inventories,
            or deal with ongoing court supervision. For many families with
            straightforward estates, it&apos;s the ideal solution. If you&apos;re
            dealing with property that has{' '}
            <a
              href="/sell-distressed-property"
              className="text-teal-600 hover:text-teal-700 font-medium"
            >
              distressed conditions
            </a>{' '}
            and want to sell quickly after using Muniment of Title, we can help.
          </p>

          <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
            Affidavit of Heirship
          </h3>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            An Affidavit of Heirship isn&apos;t technically a probate
            procedure — it&apos;s a document that can be used to establish
            ownership of real property when someone dies without a will and the
            estate doesn&apos;t go through formal probate. It&apos;s commonly
            used for older estates where the original owner died years ago and
            no probate was ever opened.
          </p>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            The affidavit is signed by someone with personal knowledge of the
            deceased&apos;s family history (not an heir) and identifies all the
            legal heirs. When recorded in the county property records, it creates
            a paper trail that title companies can use to insure the property.
            However, it&apos;s not as legally strong as a court order, and some
            title companies may require additional documentation.
          </p>

          <div className="bg-gray-50 rounded-lg p-6 mb-6">
            <h4 className="text-xl font-bold text-gray-900 mb-4">
              Which Alternative Is Right for You?
            </h4>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="bg-white p-4 rounded-lg shadow">
                <h5 className="font-bold text-gray-900 mb-2">
                  Small Estate Affidavit
                </h5>
                <p className="text-sm text-gray-600 mb-2">
                  Best for: Estates under $75,000
                </p>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Personal property transfers</li>
                  <li>• Bank accounts</li>
                  <li>• Vehicles</li>
                </ul>
              </div>
              <div className="bg-white p-4 rounded-lg shadow">
                <h5 className="font-bold text-gray-900 mb-2">
                  Muniment of Title
                </h5>
                <p className="text-sm text-gray-600 mb-2">
                  Best for: Real property with a will
                </p>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• No unpaid debts</li>
                  <li>• Clear beneficiaries</li>
                  <li>• Property transfer focus</li>
                </ul>
              </div>
              <div className="bg-white p-4 rounded-lg shadow">
                <h5 className="font-bold text-gray-900 mb-2">
                  Affidavit of Heirship
                </h5>
                <p className="text-sm text-gray-600 mb-2">
                  Best for: Old estates, no will
                </p>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Years since death</li>
                  <li>• Clear family history</li>
                  <li>• Real property only</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Executor Responsibilities Section */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            Understanding Executor Responsibilities
          </h2>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            If you&apos;ve been named as an executor in someone&apos;s will,
            you might be wondering what exactly you&apos;re supposed to do.
            Being an executor is both an honor and a significant responsibility.
            The deceased person trusted you to carry out their final wishes,
            and the law holds you to a high standard of care.
          </p>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            Your primary duties as executor include: locating and filing the
            will with the court, identifying and securing all estate assets,
            notifying creditors and paying valid debts, filing tax returns
            (both final personal returns and estate tax returns if applicable),
            managing estate assets during administration, distributing assets to
            beneficiaries according to the will, and keeping accurate records of
            all transactions.
          </p>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            You have a &quot;fiduciary duty&quot; to the estate and beneficiaries,
            meaning you must act in their best interests—not your own. You can&apos;t
            use estate assets for personal benefit, must avoid conflicts of
            interest, and should make prudent decisions about managing and
            distributing assets. If you breach these duties, you can be held
            personally liable.
          </p>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            The good news is that executors are entitled to reasonable
            compensation for their work. Texas law allows executors to receive a
            commission of up to 5% of the amounts they actually handle (money
            coming in and going out of the estate). Many family member executors
            waive this fee, but it&apos;s available if you need it.
          </p>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            If you&apos;re an executor dealing with real property in the estate,
            you&apos;ll need to decide whether to sell it, distribute it to
            beneficiaries, or handle it in some other way. Understanding the
            local real estate market is important — for El Paso properties,
            our guide on{' '}
            <a
              href="/elpaso-tx"
              className="text-teal-600 hover:text-teal-700 font-medium"
            >
              El Paso real estate resources
            </a>{' '}
            can help you understand your options.
          </p>
        </section>

        {/* Common Probate Challenges */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            Common Probate Challenges and How to Handle Them
          </h2>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            While many probate cases proceed smoothly, complications can arise.
            Being aware of potential challenges helps you prepare and respond
            appropriately.
          </p>

          <div className="space-y-6 mb-8">
            <div className="bg-gray-50 p-6 rounded-lg">
              <h4 className="text-xl font-bold text-gray-900 mb-3">
                <i className="ri-user-unfollow-line text-teal-600 mr-2"></i>
                Will Contests
              </h4>
              <p className="text-gray-700 mb-3">
                Sometimes family members challenge the validity of a will,
                claiming the deceased lacked mental capacity, was unduly
                influenced, or the will wasn&apos;t properly executed. Will
                contests can delay probate significantly and create family
                conflict.
              </p>
              <p className="text-gray-700">
                <strong>How to handle:</strong> If you anticipate a contest,
                consult with a probate litigation attorney immediately.
                Document everything and gather evidence supporting the will&apos;s
                validity.
              </p>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg">
              <h4 className="text-xl font-bold text-gray-900 mb-3">
                <i className="ri-file-unknow-line text-teal-600 mr-2"></i>
                Missing or Unclear Wills
              </h4>
              <p className="text-gray-700 mb-3">
                Sometimes the original will can&apos;t be found, or the language
                is ambiguous. Texas courts can probate a copy of a will under
                certain circumstances, but it requires additional proof. Unclear
                language may require court interpretation.
              </p>
              <p className="text-gray-700">
                <strong>How to handle:</strong> Search thoroughly for the
                original. If using a copy, be prepared to prove the original
                wasn&apos;t revoked. For ambiguous language, the court will try
                to determine the deceased&apos;s intent.
              </p>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg">
              <h4 className="text-xl font-bold text-gray-900 mb-3">
                <i className="ri-money-dollar-circle-line text-teal-600 mr-2"></i>
                Insolvent Estates
              </h4>
              <p className="text-gray-700 mb-3">
                When debts exceed assets, the estate is &quot;insolvent.&quot;
                Texas law establishes a priority order for paying creditors,
                and some beneficiaries may receive nothing. This can be
                emotionally difficult for families expecting an inheritance.
              </p>
              <p className="text-gray-700">
                <strong>How to handle:</strong> Work with an attorney to
                properly classify and prioritize debts. Some debts may be
                dischargeable or negotiable. Understand that secured debts (like
                mortgages) take priority.
              </p>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg">
              <h4 className="text-xl font-bold text-gray-900 mb-3">
                <i className="ri-group-line text-teal-600 mr-2"></i>
                Family Disputes
              </h4>
              <p className="text-gray-700 mb-3">
                Grief can bring out the worst in family dynamics. Disputes over
                who gets what, accusations of unfairness, and old resentments
                can surface during probate, making the executor&apos;s job much
                harder.
              </p>
              <p className="text-gray-700">
                <strong>How to handle:</strong> Communicate transparently with
                all beneficiaries. Document your decisions and the reasoning
                behind them. Consider mediation for serious disputes before
                they escalate to litigation.
              </p>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg">
              <h4 className="text-xl font-bold text-gray-900 mb-3">
                <i className="ri-home-gear-line text-teal-600 mr-2"></i>
                Property in Poor Condition
              </h4>
              <p className="text-gray-700 mb-3">
                Inherited properties sometimes need significant repairs,
                have been neglected, or have issues like{' '}
                <a
                  href="/elpaso-tx/sell-hoarder-houses"
                  className="text-teal-600 hover:text-teal-700"
                >
                  hoarding situations
                </a>. This can make selling through traditional channels
                difficult and expensive.
              </p>
              <p className="text-gray-700">
                <strong>How to handle:</strong> Consider selling to a cash buyer
                who purchases properties as‑is. This eliminates the need for
                repairs and can speed up the estate settlement process.
              </p>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            Frequently Asked Questions About Texas Probate
          </h2>

          <div className="space-y-6">
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                How long does probate take in Texas?
              </h3>
              <p className="text-gray-700">
                Simple estates with independent administration can be settled in
                a few months. More complex estates or those with dependent
                administration can take a year or longer. The timeline depends
                on factors like estate complexity, whether there are disputes,
                and how quickly the executor acts.
              </p>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                How much does probate cost in Texas?
              </h3>
              <p className="text-gray-700">
                Costs vary widely depending on estate complexity and whether you
                hire an attorney. Court filing fees are typically a few hundred
                dollars. Attorney fees can range from $1,500 for simple estates
                to $10,000+ for complex ones. Independent administration is
                generally less expensive than dependent administration.
              </p>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Do I need a lawyer for probate in Texas?
              </h3>
              <p className="text-gray-700">
                While not legally required, having an attorney is highly
                recommended. Probate involves legal procedures, deadlines, and
                potential pitfalls that can be costly if handled incorrectly.
                Many attorneys offer flat‑fee arrangements for straightforward
                probate cases.
              </p>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Can I sell inherited property before probate is complete?
              </h3>
              <p className="text-gray-700">
                In most cases, yes — especially with independent administration.
                The executor has authority to sell estate property once
                Letters Testamentary are issued. With dependent administration,
                court approval is required. We regularly purchase properties
                during the probate process and can work with your timeline.
              </p>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                What if the deceased owned property in multiple states?
              </h3>
              <p className="text-gray-700">
                You&apos;ll need &quot;ancillary probate&quot; in each state where
                real property is located. The primary probate occurs in the
                state where the deceased lived, and simplified proceedings can
                often be used in other states. This adds complexity and cost to
                the process.
              </p>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Are there ways to avoid probate entirely?
              </h3>
              <p className="text-gray-700">
                Yes, through proper estate planning. Tools like living trusts,
                transfer‑on‑death deeds, joint ownership with right of survivorship,
                and beneficiary designations can help assets pass outside of
                probate. However, these must be set up before death—they can&apos;t
                help after the fact.
              </p>
            </div>
          </div>
        </section>

        {/* Final CTA Section */}
        <section className="mb-16">
          <div className="bg-gradient-to-br from-teal-600 to-teal-700 rounded-2xl p-8 md:p-12 text-white">
            <div className="text-center mb-8">
              <div className="w-20 h-20 flex items-center justify-center bg-white/20 rounded-full mx-auto mb-6">
                <i className="ri-hand-heart-line text-4xl"></i>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                We&apos;re Here to Help During This Difficult Time
              </h2>
              <p className="text-xl text-teal-100 max-w-2xl mx-auto">
                Dealing with probate while grieving is hard. If you&apos;ve
                inherited property and need to sell — whether probate is
                complete or still in progress — we can help simplify the
                process.
              </p>
            </div>

            <div className="bg-white rounded-xl p-8">
              <ProbateContactForm
                formId="final-probate-form"
                title="Get Your Free Consultation"
                subtitle="No obligation, compassionate service"
              />
            </div>

            <div className="mt-8 text-center">
              <p className="text-teal-100 mb-4">Prefer to talk? Call us directly:</p>
              <a
                href="tel:+1-915-206-5814"
                className="inline-flex items-center px-8 py-4 bg-white text-teal-600 font-semibold rounded-lg hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap text-lg"
              >
                <i className="ri-phone-line mr-2"></i>(915) 206-5814
              </a>
            </div>
          </div>
        </section>

        {/* Related Resources */}
        <section className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Related Resources</h2>
          <div className="grid md:grid-cols-2 gap-4">
            <a
              href="/inherited-homes"
              className="flex items-center p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
            >
              <i className="ri-home-heart-line text-teal-600 text-2xl mr-4"></i>
              <div>
                <p className="font-semibold text-gray-900">Selling Inherited Homes</p>
                <p className="text-sm text-gray-600">
                  Complete guide to selling inherited property
                </p>
              </div>
            </a>
            <a
              href="/title-issues"
              className="flex items-center p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
            >
              <i className="ri-file-shield-2-line text-teal-600 text-2xl mr-4"></i>
              <div>
                <p className="font-semibold text-gray-900">Title Issues</p>
                <p className="text-sm text-gray-600">
                  Resolving title problems on inherited property
                </p>
              </div>
            </a>
            <a
              href="/local-news-el-paso-texas"
              className="flex items-center p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
            >
              <i className="ri-newspaper-line text-teal-600 text-2xl mr-4"></i>
              <div>
                <p className="font-semibold text-gray-900">El Paso Local News</p>
                <p className="text-sm text-gray-600">
                  Stay informed about your community
                </p>
              </div>
            </a>
            <a
              href="/get-cash-offer-for-my-property"
              className="flex items-center p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
            >
              <i className="ri-money-dollar-circle-line text-teal-600 text-2xl mr-4"></i>
              <div>
                <p className="font-semibold text-gray-900">Get Cash Offer</p>
                <p className="text-sm text-gray-600">
                  Request your no‑obligation cash offer
                </p>
              </div>
            </a>
          </div>
        </section>
      </article>

      <Footer />
    </div>
  );
}
